import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type FaqItem = { q: string; a: ReactNode };

/** Lucide-icoon per categorie (naam voor mapping in FaqPage). */
export type FaqCategoryIcon = 'scan' | 'fileText' | 'creditCard' | 'package' | 'headphones';

export type FaqCategory = { id: string; title: string; icon: FaqCategoryIcon; items: FaqItem[] };

export const FAQ_CATEGORIES: FaqCategory[] = [
  {
    id: 'scan',
    title: 'Over de ComfortTool-scan',
    icon: 'scan',
    items: [
      {
        q: 'Hoe werkt de scan?',
        a: 'Je vult basisinformatie in over je gebouw en ruimtes: oppervlaktes, isolatie, ramen, ventilatie en hoe je het graag binnen hebt. ComfortTool berekent daarmee indicatieve verwarmings- en koellast (kW) en een jaarschatting — ook per ruimte waar dat past.',
      },
      {
        q: 'Wat doet de scan precies?',
        a: 'Je krijgt inzicht in hoeveel vermogen je ongeveer nodig hebt om comfortabel te verwarmen en te koelen, plus een schatting van jaarlijks energieverbruik op basis van je invoer en (optioneel) klimaatdata bij je postcode.',
      },
      {
        q: 'Is het resultaat betrouwbaar?',
        a: 'Ja, mits je invoer klopt — maar het blijft een eerste inschatting, geen officieel energielabel of vervanger voor een gedetailleerde VABI/NTA-berekening. Voor complexe panden of beslissingen met hoge impact: laat het toetsen door een adviseur of installateur.',
      },
      {
        q: 'Wat als ik niet weet wat ik moet invullen?',
        a: 'Vul een zo goed mogelijke schatting in. Twijfel je over isolatie of ramen? Kies de optie die het dichtst in de buurt komt. Daarna kun je scenario’s vergelijken in de scan. Voor maatwerk op tekening werkt ComfortTool.nl met aparte trajecten; deze webapp focust op snelle zelfscan.',
      },
      {
        q: 'Wat is het verschil tussen Comfort Basis en Comfort Plus?',
        a: 'Comfort Basis levert een vast opgebouwd rapport. Comfort Plus voegt een uitgeschreven leesrapport toe op basis van je invoer en uitkomsten — handig om te lezen en te delen, naast de cijfers.',
      },
    ],
  },
  {
    id: 'rapport',
    title: 'Over het rapport',
    icon: 'fileText',
    items: [
      {
        q: 'Wat zit er in het rapport?',
        a: 'Je ziet samenvattingen van warmte- en koellast, jaarperspectief en vervolgstappen in begrijpelijke taal. Plus-rapporten bevatten extra toelichting en structuur; exacte opmaak kan per bundel verschillen.',
      },
      {
        q: 'Kan ik het rapport delen met mijn installateur?',
        a: 'Ja. Je kunt afdrukken, opslaan als PDF of tekst kopiëren waar de app dat toelaat. Zo neem je het makkelijk mee naar je installateur of adviseur.',
      },
      {
        q: 'Kan ik het rapport later aanpassen?',
        a: 'Je kunt binnen de bundel vaak opnieuw genereren tot de limiet die bij je aankoop hoort. Wil je andere invoer? Start een nieuwe scan of pas je opgeslagen invoer aan en bestel opnieuw indien nodig.',
      },
      {
        q: 'Voor wie is ComfortTool bedoeld?',
        a: 'Voor bewoners, eigenaren en professionals die snel zicht willen op indicatieve lasten — als startpunt, niet als enige bron voor vergunning of subsidie.',
      },
    ],
  },
  {
    id: 'bestellen',
    title: 'Bestellen en betalen',
    icon: 'creditCard',
    items: [
      {
        q: 'Hoe bestel ik Comfort Basis of Plus?',
        a: 'Doorloop de scan tot je resultaat hebt, kies je bundel en eventuele opties, en betaal veilig online. Gebruik daarna hetzelfde e-mailadres als bij de betaling om een account te maken of in te loggen — dan staat je aankoop netjes bij elkaar.',
      },
      {
        q: 'Welke betaalmethoden zijn er?',
        a: (
          <>
            Dat hangt af van wat Stripe in jouw situatie toont (land, valuta, apparaat). Bij ons staan o.a. iDEAL en Wero,
            Bancontact, BLIK, EPS, kaarten, Apple Pay, Google Pay, Link en Klarna aan. Het overzicht met iconen en uitleg
            staat op{' '}
            <Link to="/betaalmethoden" className="font-semibold text-ct-accent hover:underline">
              Betaalmethoden
            </Link>
            .
          </>
        ),
      },
      {
        q: 'Hoe koppel ik mijn aankoop aan mijn account?',
        a: 'Registreer of log in met hetzelfde e-mailadres als bij de betaling, of volg de stappen op de bedanktpagina na je aankoop. Meer uitleg staat onder Help → Account en aankoop.',
      },
    ],
  },
  {
    id: 'levering',
    title: 'Levering en digitaal product',
    icon: 'package',
    items: [
      {
        q: 'Wanneer heb ik toegang tot mijn rapport?',
        a: 'Digitale producten worden na succesvolle betaling beschikbaar in je account (rapport genereren vanuit je aankoop). Geen fysieke levering.',
      },
      {
        q: 'Kan ik annuleren of retourneren?',
        a: 'Digitale maatwerkproducten vallen doorgaans buiten het herroepingsrecht. Bij een duidelijke fout in levering of technisch probleem: neem contact op, dan lossen we het samen op.',
      },
    ],
  },
  {
    id: 'contact',
    title: 'Contact & hulp',
    icon: 'headphones',
    items: [
      {
        q: 'Ik snap het niet — wat nu?',
        a: 'Lees Help en Troubleshooting, open de digitale installatieadviseur (rechtsonder, als die voor jou aan staat), of mail ons via contact. Voor inhoudelijke bouwvragen: schakel een professional op locatie in.',
      },
      {
        q: 'Hoe snel reageren jullie?',
        a: 'We streven naar reactie binnen enkele werkdagen. Geen garantie op 24/7-support tenzij contractueel afgesproken.',
      },
    ],
  },
];
